import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  storeAuthData,
  getStoredToken,
  getStoredUser,
  clearAuthData,
  extractTokenFromUrl,
  isValidTokenFormat,
  decodeToken,
} from '../utils/auth';

interface AuthContextType {
  authToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authError: Error | null;
  loginWithOAuthCode: (token: string, user: any) => Promise<void>;
  logout: () => void;
  user: any | null;
  ssoUserId: string | null;
  username: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [user, setUser] = useState<any | null>(null);
  const [ssoUserId, setSsoUserId] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);

  // OAuth login function that accepts token and user data from OAuth callback
  const loginWithOAuthCode = async (token: string, userData: any) => {
    try {
      setIsLoading(true);

      // Store auth data
      storeAuthData({
        token,
        user: userData,
      });

      // Update state
      setAuthToken(token);
      setUser(userData);
      setSsoUserId(userData.sso_user_id || userData.id);
      setUsername(userData.username);
      setIsAuthenticated(true);
      setAuthError(null);
      setIsLoading(false);
    } catch (error) {
      const authError = error instanceof Error ? error : new Error('OAuth login failed');
      setAuthError(authError);
      setIsLoading(false);
      throw error;
    }
  };

  const logout = () => {
    setAuthToken(null);
    setUser(null);
    setSsoUserId(null);
    setUsername(null);
    setIsAuthenticated(false);
    clearAuthData();
  };

  // Initialize auth on mount
  useEffect(() => {
    const initializeAuth = async () => {
      // Development bypass - automatically authenticate in dev mode
      // Set VITE_BYPASS_AUTH=false in .env.local to disable this
      const bypassAuth = import.meta.env.VITE_BYPASS_AUTH !== 'false' && import.meta.env.DEV;

      if (bypassAuth) {
        // Set mock user data for development
        const mockUser = {
          id: 'dev-user-1',
          sso_user_id: 'dev-user-1',
          username: 'dev-user',
        };
        const mockToken = 'dev-token-' + Date.now();

        storeAuthData({ token: mockToken, user: mockUser });
        setAuthToken(mockToken);
        setUser(mockUser);
        setSsoUserId(mockUser.sso_user_id);
        setUsername(mockUser.username);
        setIsAuthenticated(true);
        setAuthError(null);
        setIsLoading(false);
        return;
      }

      // Check for token in URL query string (DraftKings direct SSO flow)
      const urlToken = extractTokenFromUrl();
      if (urlToken && isValidTokenFormat(urlToken)) {
        const decoded = decodeToken(urlToken);
        if (decoded) {
          const ssoId = decoded.external_id || decoded.sso_user_id || decoded.user_id || '';
          const userData = {
            id: ssoId,
            sso_user_id: ssoId,
            username: decoded.username || '',
          };
          storeAuthData({ token: urlToken, user: userData });
          setAuthToken(urlToken);
          setUser(userData);
          setSsoUserId(ssoId);
          setUsername(userData.username);
          setIsAuthenticated(true);
          setAuthError(null);
          setIsLoading(false);
          return;
        }
      }

      // Check for stored authentication
      const storedToken = getStoredToken();
      const storedUser = getStoredUser();

      // Check if we should force logout (for testing)
      const forceLogout = import.meta.env.VITE_FORCE_LOGOUT === 'true';
      if (forceLogout) {
        clearAuthData();
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      if (storedToken && storedUser) {
        setAuthToken(storedToken);
        setUser(storedUser);
        setSsoUserId(storedUser.sso_user_id || storedUser.id);
        setUsername(storedUser.username);
        setIsAuthenticated(true);
        setAuthError(null);
        setIsLoading(false);
        return;
      }

      // No authentication found - user needs to login via OAuth
      setIsAuthenticated(false);
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        authToken,
        isAuthenticated,
        isLoading,
        authError,
        loginWithOAuthCode,
        logout,
        user,
        ssoUserId,
        username,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
